import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  commentReactionListSchema,
  reactionEmojiSchema,
  type ActivityFeed,
  type Comment,
  type CommentReactionList,
  type CommentStream,
  type ReactionEmoji,
} from '@molt/shared';

import { get, put, request } from './api';
import { queryKeys } from './query-keys';

export interface ToggleCommentReactionVariables {
  readonly cardId: string;
  readonly commentId: string;
  readonly emoji: ReactionEmoji;
  readonly action: 'add' | 'remove';
}

export function getCommentReactions(commentId: string): Promise<CommentReactionList> {
  return get(
    `/comments/${encodeURIComponent(commentId)}/reactions`,
    commentReactionListSchema,
  );
}

export function addCommentReaction(
  commentId: string,
  emoji: ReactionEmoji,
): Promise<CommentReactionList> {
  return put(
    `/comments/${encodeURIComponent(commentId)}/reactions`,
    commentReactionListSchema,
    { emoji: reactionEmojiSchema.parse(emoji) },
  );
}

export function removeCommentReaction(
  commentId: string,
  emoji: ReactionEmoji,
): Promise<CommentReactionList> {
  return request(
    `/comments/${encodeURIComponent(commentId)}/reactions`,
    commentReactionListSchema,
    {
      method: 'DELETE',
      body: JSON.stringify({ emoji: reactionEmojiSchema.parse(emoji) }),
    },
  );
}

export function replaceCommentReactions(
  comment: Comment,
  commentId: string,
  reactions: CommentReactionList,
): Comment {
  return comment.id === commentId ? { ...comment, reactions } : comment;
}

export function replaceCommentReactionsInStream(
  stream: CommentStream | undefined,
  commentId: string,
  reactions: CommentReactionList,
): CommentStream | undefined {
  return stream === undefined
    ? stream
    : {
        ...stream,
        comments: stream.comments.map((comment) =>
          replaceCommentReactions(comment, commentId, reactions),
        ),
      };
}

export function replaceCommentReactionsInActivity(
  feed: ActivityFeed | undefined,
  commentId: string,
  reactions: CommentReactionList,
): ActivityFeed | undefined {
  return feed === undefined
    ? feed
    : {
        ...feed,
        entries: feed.entries.map((entry) =>
          entry.comment === null
            ? entry
            : {
                ...entry,
                comment: replaceCommentReactions(entry.comment, commentId, reactions),
              },
        ),
      };
}

export function useToggleCommentReaction(): UseMutationResult<
  CommentReactionList,
  Error,
  ToggleCommentReactionVariables
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ commentId, emoji, action }) =>
      action === 'add'
        ? addCommentReaction(commentId, emoji)
        : removeCommentReaction(commentId, emoji),
    onSuccess: (reactions, variables) => {
      queryClient.setQueryData<CommentStream>(
        queryKeys.cards.comments(variables.cardId),
        (stream) =>
          replaceCommentReactionsInStream(stream, variables.commentId, reactions),
      );
      queryClient.setQueryData<ActivityFeed>(
        queryKeys.cards.activity(variables.cardId, 'all'),
        (feed) => replaceCommentReactionsInActivity(feed, variables.commentId, reactions),
      );
      queryClient.setQueryData<ActivityFeed>(
        queryKeys.cards.activity(variables.cardId, 'comments'),
        (feed) => replaceCommentReactionsInActivity(feed, variables.commentId, reactions),
      );
    },
  });
}
